import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  ICategory,
  IProduct,
  IProductInfo,
  IRetailerInfo,
  IRoomInfo,
} from 'interfaces';
import { pos365Hooks } from 'hooks';
import useSWR from 'swr';
import FirebaseService from '../services/apis/firebase';
import { notification } from 'antd';

interface RoomContextState {
  roomCode: string;
  retailerInfo: IRetailerInfo | undefined;
  totalOrderedPrice: number;
  categoryList: ICategory[];
  isLoadingCategoryList: boolean;
  isLoadingRoomInfo: boolean;
  setSelectedCategoryId: (id: number) => void;
  roomName: string;
  orderedProductList: IProductInfo[];
  totalOrderItem: number;
  selectedCategoryId: number | undefined;
  reloadAllData: () => Promise<void>;
  productList: IProduct[] | null | undefined;
  isLoadingProductList: boolean;
}

const defaultRetailerInfo = {
  id: 0,
  name: '',
  code: '',
  phone: '',
};

const defaultRoomContext: RoomContextState = {
  roomCode: '',
  retailerInfo: defaultRetailerInfo,
  totalOrderedPrice: 0,
  categoryList: [],
  productList: [],
  isLoadingProductList: false,
  isLoadingCategoryList: false,
  isLoadingRoomInfo: false,
  setSelectedCategoryId: () => {},
  roomName: '',
  orderedProductList: [],
  totalOrderItem: 0,
  selectedCategoryId: 0,
  reloadAllData: async () => {},
};

const RoomContext = createContext<RoomContextState>(defaultRoomContext);

interface RoomProviderProps {
  roomCode: string;
  children: React.ReactNode;
}

const { usePos365 } = pos365Hooks;

export const RoomProvider: React.FC<RoomProviderProps> = ({
  roomCode,
  children,
}: RoomProviderProps) => {
  // Room info
  const [retailerInfo, setRetailerInfo] = useState<IRetailerInfo>(
    defaultRetailerInfo
  );
  const [roomInfo, setRoomInfo] = useState<IRoomInfo>({
    id: 0,
    name: '',
    branchId: 0,
  });
  const [totalOrderedPrice, setTotalOrderedPrice] = useState<number>(0);
  const [orderedProductList, setOrderedProductList] = useState<IProductInfo[]>(
    []
  );

  // Category and product list of this room
  const [categoryList, setCategoryList] = useState<ICategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>();
  const [isLoadingRoomInfo, setIsLoadingRoomInfo] = useState<boolean>(false);
  const [isLoadingCategoryList, setIsLoadingCategoryList] = useState<boolean>(
    false
  );

  const { getCategories, getProductByCategoryId, getRoomInfo } = usePos365();

  // use SWR for product list because there are many categoryId
  const {
    data: productList,
    isLoading: isLoadingProductList,
    mutate: loadProductList,
  } = useSWR(
    `/product/${selectedCategoryId}`,
    async () => {
      if (selectedCategoryId === undefined) return;
      return getProductByCategoryId(retailerInfo.code, selectedCategoryId);
    },
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
    }
  );

  const loadRoomInfo = async () => {
    try {
      setIsLoadingRoomInfo(true);
      const roomInfo = await getRoomInfo(roomCode);

      if (!!roomInfo) {
        setRetailerInfo(roomInfo.retailerInfo);
        setTotalOrderedPrice(roomInfo.totalPrice);
        setRoomInfo({
          id: roomInfo.id,
          name: roomInfo.name,
          branchId: roomInfo.branchInfo.id,
        });
        setOrderedProductList(roomInfo.orderedProductList ?? []);
      }
    } finally {
      setIsLoadingRoomInfo(false);
    }
  };

  const loadCategoryList = async () => {
    if (!retailerInfo.code) return;

    try {
      setIsLoadingCategoryList(true);
      const result = await getCategories(retailerInfo.code);

      if (!!result && !!result.length) {
        setCategoryList([
          { id: 0, name: 'TẤT CẢ', parentId: '', description: '' },
          ...result,
        ]);
        // auto select the first category tab (all products)
        setSelectedCategoryId(0);
      }
    } finally {
      setIsLoadingCategoryList(false);
    }
  };

  // reload all data
  const reloadAllData = async () => {
    await loadRoomInfo();
    await loadCategoryList();
    await loadProductList();
  };

  // Effect 1: Load room info by room code
  useEffect(() => {
    loadRoomInfo().then();
  }, [roomCode]);

  // Effect 2: Load category list based on retailerCode
  useEffect(() => {
    loadCategoryList().then();
  }, [retailerInfo.code]);

  const handleChange = async () => {
    await loadRoomInfo();
    notification.success({
      message: 'Đồng bộ dữ liệu thành công',
    });
  };

  useEffect(() => {
    if (roomInfo.id === 0 || roomInfo.branchId === 0 || retailerInfo.id === 0)
      return;
    FirebaseService.connect(
      `${roomInfo.branchId}_${retailerInfo.id}/${roomInfo.id}_A`,
      handleChange
    );
  }, [roomInfo.id, roomInfo.branchId, retailerInfo.id]);

  // Effect 3: Load product list based on selected category
  // useEffect(() => {
  //   loadProductList().then();
  // }, [selectedCategoryId]);

  return (
    <RoomContext.Provider
      value={{
        roomCode,
        retailerInfo,
        totalOrderedPrice,
        categoryList,
        productList,
        isLoadingProductList,
        isLoadingCategoryList,
        isLoadingRoomInfo,
        setSelectedCategoryId,
        roomName: roomInfo.name,
        orderedProductList,
        get totalOrderItem() {
          return orderedProductList.length;
        },
        selectedCategoryId,
        reloadAllData,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
};

export const useRoom = () => {
  return useContext(RoomContext);
};
